import React, { useEffect, useState, useCallback } from 'react';
import toast from 'react-hot-toast';
import { customerAPI } from '../services/api';
import { Button, Modal, Spinner, EmptyState, PageHeader, Badge } from '../components/Common';

const emptyForm = { name:'', phone:'', whatsapp:'', email:'', address:'', notes:'', gstin:'', state:'', state_code:'' };

const CustomersPage = () => {
  const [customers, setCustomers] = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [search,    setSearch]    = useState('');
  const [showModal, setShowModal] = useState(false);
  const [viewModal, setViewModal] = useState(null);
  const [editId,    setEditId]    = useState(null);
  const [form,      setForm]      = useState(emptyForm);
  const [saving,    setSaving]    = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await customerAPI.getAll({ search });
      setCustomers(res.data.customers);
    } catch { toast.error('Customers load nahi hue'); }
    finally { setLoading(false); }
  }, [search]);

  useEffect(() => { load(); }, [load]);

  const openAdd  = () => { setForm(emptyForm); setEditId(null); setShowModal(true); };
  const openEdit = (c) => {
    setForm({ 
      name:c.name, phone:c.phone, whatsapp:c.whatsapp||'', 
      email:c.email||'', address:c.address||'', notes:c.notes||'',
      gstin:c.gstin||'', state:c.state||'', state_code:c.state_code||''
    });
    setEditId(c.id); setShowModal(true);
  };

  const openView = async (id) => {
    try {
      const res = await customerAPI.getOne(id);
      setViewModal(res.data);
    } catch { toast.error('Details nahi mili'); }
  };

  const handleSave = async () => {
    if (!form.name || !form.phone) { toast.error('Name aur Phone required hai'); return; }
    setSaving(true);
    try {
      if (editId) {
        await customerAPI.update(editId, form);
        toast.success('Customer update ho gaya! ✅');
      } else {
        await customerAPI.create(form);
        toast.success('Customer add ho gaya! ✅');
      }
      setShowModal(false); load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error aaya');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`"${name}" delete karna chahte ho?`)) return;
    try {
      await customerAPI.delete(id);
      toast.success('Customer delete ho gaya');
      load();
    } catch { toast.error('Delete nahi hua'); }
  };

  const f = (field, val) => setForm(p => ({ ...p, [field]: val }));

  return (
    <div className="page">
      <PageHeader
        title="👥 Customers"
        subtitle={`${customers.length} registered`}
        actions={<Button onClick={openAdd}>+ Add Customer</Button>}
      />

      <div className="search-wrap" style={{ marginBottom:20, maxWidth:360 }}>
        <span className="search-icon">🔍</span>
        <input className="form-input" placeholder="Name, phone search..." value={search} onChange={e => setSearch(e.target.value)} style={{ paddingLeft:34 }} />
      </div>

      <div className="card">
        <div className="table-wrap">
          {loading ? <Spinner /> : customers.length === 0 ? (
            <EmptyState icon="👥" title="Koi customers nahi" message="'+ Add Customer' se add karo" />
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Name</th><th>Phone</th><th>WhatsApp</th>
                  <th>Email</th><th>Orders</th><th>Total Spent</th><th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {customers.map(c => (
                  <tr key={c.id}>
                    <td><strong>{c.name}</strong></td>
                    <td>{c.phone}</td>
                    <td style={{ color:'var(--accent2)' }}>📱 {c.whatsapp||'—'}</td>
                    <td style={{ color:'var(--text2)', fontSize:12 }}>{c.email||'—'}</td>
                    <td><Badge variant="blue">{c.total_purchases} orders</Badge></td>
                    <td><strong style={{ color:'var(--accent2)' }}>₹{parseFloat(c.total_spent||0).toLocaleString('en-IN')}</strong></td>
                    <td>
                      <div style={{ display:'flex', gap:6 }}>
                        <button className="btn btn-ghost btn-sm" onClick={() => openView(c.id)}>👁 View</button>
                        <button className="btn btn-ghost btn-sm" onClick={() => openEdit(c)}>✏️</button>
                        <button className="btn btn-sm" style={{ background:'rgba(255,107,107,.15)', color:'var(--danger)' }} onClick={() => handleDelete(c.id, c.name)}>🗑</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Add/Edit Modal */}
      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title={editId ? '✏️ Customer Edit' : '👤 New Customer'}>
        <div className="form-group">
          <label className="form-label">Full Name *</label>
          <input className="form-input" placeholder="Customer ka naam" value={form.name} onChange={e => f('name', e.target.value)} />
        </div>
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Phone *</label>
            <input className="form-input" placeholder="10-digit" maxLength={10} value={form.phone} onChange={e => f('phone', e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">WhatsApp</label>
            <input className="form-input" placeholder="Same as phone?" value={form.whatsapp} onChange={e => f('whatsapp', e.target.value)} />
          </div>
        </div>
        <div className="form-group">
          <label className="form-label">Email</label>
          <input className="form-input" type="email" placeholder="email@example.com" value={form.email} onChange={e => f('email', e.target.value)} />
        </div>
        <div className="form-group">
          <label className="form-label">Address</label>
          <input className="form-input" placeholder="Optional" value={form.address} onChange={e => f('address', e.target.value)} />
        </div>
        <div className="form-group">
          <label className="form-label">GSTIN</label>
          <input className="form-input" placeholder="22AAAAA0000A1Z5" value={form.gstin} onChange={e => f('gstin', e.target.value)} />
        </div>
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">State</label>
            <input className="form-input" placeholder="Rajasthan" value={form.state} onChange={e => f('state', e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">State Code</label>
            <input className="form-input" placeholder="08" value={form.state_code} onChange={e => f('state_code', e.target.value)} />
          </div>
        </div>
        <div className="modal-footer">
          <Button variant="ghost" onClick={() => setShowModal(false)}>Cancel</Button>
          <Button onClick={handleSave} loading={saving}>💾 {editId ? 'Update' : 'Save'}</Button>
        </div>
      </Modal>

      {/* View Modal */}
      <Modal isOpen={!!viewModal} onClose={() => setViewModal(null)} title={`👤 ${viewModal?.customer?.name}`} size="lg">
        {viewModal && (
          <>
            <div className="form-row" style={{ marginBottom:20 }}>
              {[
                ['📱 Phone',    viewModal.customer.phone],
                ['💬 WhatsApp', viewModal.customer.whatsapp||'—'],
                ['✉️ Email',    viewModal.customer.email||'—'],
                ['💰 Total',    `₹${parseFloat(viewModal.customer.total_spent||0).toLocaleString('en-IN')}`],
              ].map(([label, val]) => (
                <div key={label} style={{ background:'var(--bg3)', borderRadius:8, padding:12 }}>
                  <div style={{ fontSize:11, color:'var(--text3)', marginBottom:4 }}>{label}</div>
                  <div style={{ fontWeight:600 }}>{val}</div>
                </div>
              ))}
            </div>
            <div style={{ fontWeight:600, marginBottom:12 }}>
              Purchase History ({viewModal.invoices?.length||0})
            </div>
            {(viewModal.invoices||[]).length === 0 ? (
              <div style={{ color:'var(--text3)', textAlign:'center', padding:20 }}>
                Koi purchases nahi
              </div>
            ) : (
              <table>
                <thead><tr><th>Invoice</th><th>Items</th><th>Total</th><th>Date</th></tr></thead>
                <tbody>
                  {viewModal.invoices.map(inv => (
                    <tr key={inv.id}>
                      <td style={{ color:'var(--accent)', fontSize:12 }}>{inv.invoice_number}</td>
                      <td style={{ fontSize:12 }}>{inv.items?.length} items</td>
                      <td style={{ color:'var(--accent2)', fontWeight:700 }}>₹{parseFloat(inv.total_amount).toLocaleString('en-IN')}</td>
                      <td style={{ color:'var(--text2)', fontSize:12 }}>{new Date(inv.createdAt).toLocaleDateString('en-IN')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            <div className="modal-footer">
              <Button variant="ghost" onClick={() => setViewModal(null)}>Close</Button>
            </div>
          </>
        )}
      </Modal>
    </div>
  );
};

export default CustomersPage;